import { useState, useEffect, useRef } from "react";
// import axios from "axios";
import axiosJWT from "../services/axios";

import { Role, _AuthType, _WindowMode } from "../enums";
import { useHistory, useLocation } from "react-router-dom";
import { Auth } from "aws-amplify";
import { logger } from "../services/logger";
import { ErrorToast, InfoToast } from "../components/v2/utils/Toast";
import { HandleUserLogout, sleep } from "../Helpers";
import { AxiosError } from "axios";
import { AuthorizerContext } from "@backend-types";
import { usePageVisibility } from "./usePageVisibility";
import useSessionState from "./useSessionState";
import useStorageState from "./useStorageState";
import postRobot from "post-robot";
// import useActivityListener from "./useActivityListener";
// import { formatISO } from "date-fns";

/**
 *
 * @returns A function that resolves to `true` if the session is authenticated with Cognito or LTI.
 */
export function useMacsAuthenticator() {
  // Locations/Params
  const location = useLocation();
  const history = useHistory();
  const tabVisible = usePageVisibility();
  let queryParams = new URLSearchParams(location.search);
  const qCode = queryParams.get("code");
  const qLtik = queryParams.get("ltik");
  const qExpStr = queryParams.get("exp");
  const qSession = queryParams.get("session");
  const qLaunchedAssignmentId = queryParams.get("canvasAssignmentId");
  // default provider at root route needed to differentiate between code coming from Canvas API vs ClassLink
  // ClassLink SSO launches to root route while Canvas API launches to sync route
  const defaultProvider = location.pathname === "/" && qCode ? "classlink" : undefined;
  const qProvider = queryParams.get("provider") ?? defaultProvider;
  const qStudentClassId = queryParams.get("classId");

  // Auth details
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  const [authType, setAuthType] = useSessionState<_AuthType | null>({
    sessionKey: "authType",
  });

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<Role | null>(null);
  const [iframeRef, setIframeRef] = useState<React.RefObject<HTMLIFrameElement> | null>(
    null
  );
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const getInitialExp = () => {
    const lExpStr = sessionStorage.getItem("exp");

    if (!Number.isNaN(Number(qExpStr)) && Number(qExpStr) !== 0) {
      return Number(qExpStr);
    } else if (!Number.isNaN(Number(lExpStr)) && Number(lExpStr) !== 0) {
      return Number(lExpStr);
    } else {
      return null;
    }
  };
  const [exp, setExp] = useSessionState<number | null>({
    sessionKey: "exp",
    initialValue: getInitialExp(),
  });

  const [refreshesRemaining, setRefreshesRemaining] = useState<null | number>(null);
  // UI Controls
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  const [showSessionEndDialog, setShowSessionEndDialog] = useState(false);
  const [sessionHasEnded, setSessionHasEnded] = useState(false);
  // LTI
  const [ltiReturnUrl, setLtiReturnUrl] = useState<string | null>(null);
  const [windowMode, setWindowMode] = useState<_WindowMode | null>(null);
  const [timeRemaining, setTimeRemaining] = useState("");

  const [sessionId, setSessionId] = useSessionState<string | null>({
    sessionKey: "sessionId",
    initialValue: qSession,
  });

  const [isStudentView, setIsStudentView] = useSessionState<string | null>({
    sessionKey: "isStudentView",
    initialValue: null,
  });

  const [exitStudentViewPath, setExitStudentViewPath] = useSessionState<string | null>({
    sessionKey: "exitStudentViewPath",
    initialValue: null,
  });

  const [launchedAssignmentId, setLaunchedAssignmentId] = useSessionState<string | null>({
    sessionKey: "launchedAssignmentId",
    initialValue: qLaunchedAssignmentId,
  });
  //
  const [code, setCode] = useSessionState<string | null>({
    sessionKey: "code",
    initialValue: qCode,
  });
  useEffect(() => {
    if (code) {
      // in an SSO session so log out Cognito
      Auth.signOut();
    }
  }, [code]);
  const [codeProvider] = useSessionState<string | null>({
    sessionKey: "codeProvider",
    initialValue: qProvider,
  });
  const [ltik, setLtik] = useSessionState<string | null>({
    sessionKey: "ltik",
    initialValue: qLtik,
  });
  useEffect(() => {
    if (ltik) {
      // in an LTI session so log out Cognito
      Auth.signOut();
    }
  }, [ltik]);

  // student current class tracking - needs to be set immediately at login
  const [studentCurrentClassId, setStudentCurrentClassId] = useStorageState({
    storageKey: "studentCurrentClassId",
    initialValue: qStudentClassId,
  });

  // session expiration handling
  useEffect(() => {
    if (!exp) return;
    // Define a constant for five minutes in milliseconds
    const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const logoutTime = exp - FIVE_MINUTES_IN_MS;
      let remaining = logoutTime - now;

      // If the remaining time is negative, set it to zero
      if (remaining < 0) {
        remaining = 0; // This ensures we never show negative numbers
      }

      // Time calculations for minutes and seconds
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
      const formattedTime = `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
      setTimeRemaining(formattedTime);

      // update toasts manually as react-toastify doesn't seem to have dynamic prop passing/rendering
      const timers = document.querySelectorAll(".digit-expiration-timer");
      timers.forEach((timer) => {
        timer.innerHTML = formattedTime;
      });

      // If the count down is finished, clear the interval
      if (remaining === 0) {
        setTimeRemaining("0:00");
        clearInterval(interval);
      }
    }, 1000);

    // Clear interval on component unmount
    return () => clearInterval(interval);
  }, [exp, tabVisible]);

  // Inactivity Listener
  // const onIdle = () => {
  //   setIdle(true);
  //   console.log("Is Idle");
  // };
  // const activityDebounce = useRef<NodeJS.Timeout | null>(null);
  // const onActivity = useCallback(() => {
  //   const source = axios.CancelToken.source();
  //   if (idle) {
  //     setIdle(false);
  //     console.log("Is Active");
  //     if (
  //       !showSessionEndDialog &&
  //       !showExtensionDialog &&
  //       !sessionHasEnded &&
  //       authType !== _AuthType.COGNITO &&
  //       authenticated
  //     ) {
  //       activityDebounce.current = setTimeout(() => {
  //         try {
  //           let data = {
  //             last_access_at: formatISO(new Date()),
  //           };
  //           axiosJWT.patch("/user/last-access-at", data, { cancelToken: source.token });
  //         } catch (err) {
  //           logger.error(err);
  //         }
  //       }, 1000);
  //     }
  //   }
  //   return () => {
  //     source.cancel("Component unmounted or dependencies changed"); // Cancel the request on cleanup
  //     if (activityDebounce.current) {
  //       clearTimeout(activityDebounce.current);
  //     }
  //   };
  // }, [
  //   idle,
  //   authType,
  //   authenticated,
  //   showSessionEndDialog,
  //   showExtensionDialog,
  //   sessionHasEnded,
  // ]);
  // useActivityListener({ onIdle, onActivity, authType });

  // useEffect(() => {
  //   if (!ltik) {
  //     console.warn("LTIK use effect triggered MacsAuthenticator", ltik);
  //     if (!qLtik) return;
  //     console.warn("About to set LTIk in MacsAuthenticator", ltik);
  //     setLtik(qLtik);
  //   }
  // }, [qLtik, ltik]);

  useEffect(() => {
    // Determine if the app is running in an iframe or directly
    const mode = window.self === window.top ? _WindowMode.TAB : _WindowMode.IFRAME;
    setWindowMode(mode);
    console.log("Window Mode: ", mode);
  }, []);

  // this ensures the ltik is always within the query params to allow refreshing tab
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ltikCheck = params.get("ltik");
    if (!ltikCheck && ltik) {
      console.warn(
        "Appending LTIK to query params to allow refreshing tab only for student role"
      );
      params.set("ltik", ltik);
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    }
  }, [location.search, location.pathname, history, ltik]);

  // const finalLogoutTimer = useRef<NodeJS.Timeout | null>(null);
  const warningTimer = useRef<NodeJS.Timeout | null>(null);
  const logoutTimer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    console.warn(
      "useEffect triggered MacsAuthenticator timers updating: ",
      exp,
      refreshesRemaining
    );

    if (!exp || Number.isNaN(Number(exp)) || Number(exp) === 0) {
      console.error("Failed to set expiration timers");
      return;
    }

    // Define the timers outside so we can clear them in the return function

    const triggerLogout = () => {
      console.warn("Logout starting");
      setShowExtensionDialog(false);
      setSessionHasEnded(true);
      // Per ticket 4744, removing this message and timer
      setShowSessionEndDialog(true);
      HandleUserLogout({
        clearCookies: true,
        clearSession: true,
        redirectToSessionEnd: true,
      });

      // ErrorToast(
      //   "Your learning session has expired, you will be logged out in 1 minute. Please re-launch the assignment from your class portal (i.e. Canvas, Studyforge)."
      // );

      // finalLogoutTimer.current = setTimeout(() => {
      //   // Assuming setShowSessionEndDialog and HandleUserLogout are defined elsewhere
      //   setShowSessionEndDialog(true);
      //   HandleUserLogout(true);
      // }, 60000); // 1 minute delay per Valeria's request
    };

    // Calculate the warning and logout times
    const warningTime = exp - 10 * 60 * 1000; // 10 minutes before expiration but we tell users it expires in 5 minutes
    const logoutTime = exp - 5 * 60 * 1000; // 5 minutes before expiration

    // Current time in milliseconds
    const now = Date.now();
    if (now >= exp) {
      console.warn("Immediate logout required as token has expired");
      triggerLogout();
      return;
    }

    // Set the timers if it's not too late
    if (warningTime > now) {
      warningTimer.current = setTimeout(() => {
        if (refreshesRemaining !== null) {
          // exit the fullscreen video
          try {
            const iframe = iframeRef?.current;
            postRobot
              .send(
                iframe?.contentWindow!,
                "exitFullScreen",
                {
                  exitFullScreen: true,
                },
                {
                  domain: "*",
                }
              )
              .then(function (event: any) {
                console.log("Sending exitFullscreen message");
              })
              .catch(function (err: any) {
                console.log("error sending exitFullscreen");
                console.error("Error", err.message);
              });
          } catch (e) {
            console.warn("Likely no iframe to send exitFullscreen message to");
          }
          if (refreshesRemaining < 1) {
            console.warn("Session expiring soon");
            InfoToast(
              <>
                Your learning session will expire in{" "}
                <span style={{ fontWeight: "bold" }}>
                  <span className="digit-expiration-timer">{""}</span> minutes
                </span>
                , please submit your work and re-launch the assignment from your class
                portal (i.e. Canvas, Studyforge). Your progress will be saved.
              </>,
              { autoClose: false, closeButton: true, closeOnClick: false }
            );
          } else {
            // refreshes are allowed on current token show extension dialog
            setShowExtensionDialog(true);
          }
        }
      }, Math.max(warningTime - now, 0));
    }

    if (logoutTime > now) {
      logoutTimer.current = setTimeout(() => {
        triggerLogout();
      }, Math.max(logoutTime - now, 0));
    }

    // This will clear the timers if the component is unmounted or `exp` changes
    return () => {
      if (warningTimer.current) {
        clearTimeout(warningTimer.current);
      }
      if (logoutTimer.current) {
        clearTimeout(logoutTimer.current);
      }
      // if (finalLogoutTimer.current) {
      //   clearTimeout(finalLogoutTimer.current);
      // }
    };
  }, [exp, iframeRef, refreshesRemaining, tabVisible]);

  const responseType = queryParams.get("response_type");
  if (responseType === "code") {
    console.log("Classlink type", code);
    queryParams.delete("code");
    queryParams.delete("response_type");
    const newUrl = `${location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  }
  const checkAuthStatus = async (refresh?: boolean) => {
    try {
      let response = await axiosJWT.get(`/lti/validation`, {
        params: {
          ...(codeProvider && code && { code: code }),
          ...(ltik && { ltik: ltik }),
          ...(refresh && { tokenAction: "refresh" }),
          ...(qProvider && { provider: codeProvider }),
        },
      });

      if (response.status === 200 && !response.data.errorMessage) {
        const data = response.data as AuthorizerContext;
        const {
          tokenExp,
          tokenRefreshResult,
          tokenRefreshesRemaining,
          tokenSessionId,
          forceLogout,
          ltiReturnUrl,
        } = data;
        console.log(
          "Validation response: ",
          response.data,
          tokenExp,
          tokenRefreshesRemaining,
          tokenRefreshResult,
          ltiReturnUrl
        );

        // if (tokenSessionId) {
        //   setSessionId(tokenSessionId);
        // }

        if (tokenSessionId && code && tokenSessionId === code) {
          setSessionId(tokenSessionId);
        }

        // expiration timestamp
        if (tokenExp && !Number.isNaN(Number(tokenExp)) && Number(tokenExp) !== 0) {
          console.warn("Setting session expiration via validation", Number(tokenExp));
          setExp(Number(tokenExp));
        }
        // refreshes remaining
        if (
          tokenRefreshesRemaining !== null &&
          tokenRefreshesRemaining !== undefined &&
          !Number.isNaN(Number(tokenRefreshesRemaining))
        ) {
          console.warn(
            "Setting session refreshes via validation",
            tokenRefreshesRemaining
          );
          setRefreshesRemaining(tokenRefreshesRemaining);
        }
        // refresh status
        if (tokenRefreshResult === "success") {
          console.warn("Successfully renewed JWT");
        } else if (tokenRefreshResult === "failed") {
          console.warn("Failed to renew JWT");
        }

        if (forceLogout) {
          console.log(
            "cognito user already logged in logging it out because sso is trying to authenticate"
          );
          try {
            await Auth.signOut();
          } catch (e) {
            console.log("error in signing out cognito user", e);
            logger.error(new Error(`error in signing out cognito user ${e}`));
            return;
          }
        }
        setAuthenticated(true);
        setCurrentUserId(data.userContext.id);
        setCurrentUserRole(data.userContext.role as Role);
        setAuthType(data.authType[0]);
        console.log(`Validation endpoint return URL: ${data.ltiReturnUrl}`);
        setLtiReturnUrl(data.ltiReturnUrl ?? null);
      } else {
        setAuthenticated(false);
        setLtiReturnUrl(null);
        setCurrentUserId(null);
        setCurrentUserRole(null);
        setAuthType(null);
        sessionStorage.clear();
      }
    } catch (err) {
      setAuthenticated(false);
      setLtiReturnUrl(null);
      setCurrentUserId(null);
      setCurrentUserRole(null);
      setAuthType(null);
      // redirect to session ended page
      // This only occurs if the request gets past the Authorizer and reaches the validation endpoint
      // At this point validation would kick you out if ltik doesn't match or is missing
      if (
        err instanceof AxiosError &&
        err.response?.status === 401 &&
        err.response?.data?.redirectPath
      ) {
        console.warn("validation endpoint reached but triggered session end");
        HandleUserLogout({
          clearCognito: true,
          clearSession: true,
          redirectToSessionEnd: true,
        });
        // const currentUrl = new URL(window.location.href);
        // const redirectPath = err.response?.data?.redirectPath;
        // // Check if the current URL is not already the session-ended page
        // if (!currentUrl.pathname.endsWith(redirectPath)) {
        //   // Replace '/session-ended' with the actual path of your session-ended page
        //   currentUrl.searchParams.delete("exp");
        //   currentUrl.searchParams.delete("ltik");
        //   MacsAuthenticator.authHook.setAuthStatus(false);
        //   MacsAuthenticator.authHook.setAuthType(null);
        //   MacsAuthenticator.authHook.setInitialized(false);
        //   MacsAuthenticator.authHook.setLtik(null);
        //   const redirectUrl = new URL(redirectPath, currentUrl); // Assuming redirectPath is a relative URL
        //   redirectUrl.searchParams.delete("exp");
        //   redirectUrl.searchParams.delete("ltik");
        //   window.location.href = redirectUrl.toString();
        // }
      }
      console.log(err);
    }
  };

  useEffect(() => {
    if (!initialized) {
      checkAuthStatus().then(() => {
        setInitialized(true);
        if (authType === _AuthType.LTI) {
          if (!ltik) {
            console.warn("LTIK not found in MacsAuthenticator");
            setShowExtensionDialog(false);
            setSessionHasEnded(true);
            ErrorToast(
              "Your LTI Launch has expired or is invalid. Please re-launch the assignment from your class portal (i.e. Canvas, Studyforge), or you can log in with your username and password. You will be redirected in 5 seconds. Thank you!"
            );
            // LTI launch is expired so force logging out user to login again this will clear LTI cookie so that user can login again via LTI or by using email and password credentials. user need not to manually clear browser cookies
            sleep(5000).then(() => {
              setShowExtensionDialog(false);
              setShowSessionEndDialog(true);
              HandleUserLogout({
                clearCookies: true,
                clearSession: true,
                redirectToSessionEnd: true,
              });
            });
            return;
          }
        }
        setInitialized(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  const handleExtensionPromptLogOut = () => {
    const iframe = document.querySelector("iframe") as HTMLIFrameElement | null;

    const payload = { userLogOut: true };
    const domainObject = { domain: "*" };

    setIsLoggingOut(true);

    const performLogout = () => {
      setShowExtensionDialog(false);
      setShowSessionEndDialog(true);
      HandleUserLogout({
        clearCookies: true,
        clearSession: true,
        redirectToSessionEnd: true,
      });

      setIsLoggingOut(false);
    };

    try {
      postRobot
        .send(iframe?.contentWindow!, "userLogOut", payload, domainObject)
        .then(() => console.log("postRobot message sent"))
        .catch((err: any) => console.error("Error sending postRobot message", err))
        .finally(() => {
          // Wait 2.5s after sending/failing the message before logging out
          setTimeout(performLogout, 2500);
        });
    } catch (e) {
      console.warn("Likely no iframe to send postRobot message to");
      // Fallback: still logout after 2.5s
      setTimeout(performLogout, 2500);
    }
  };

  const handleExtensionPromptExtend = async () => {
    console.log("Extending session");
    setShowExtensionDialog(false);
    try {
      // let data = {
      //   last_access_at: formatISO(new Date()),
      // };
      // axiosJWT.patch("/user/last-access-at", data);
    } catch (err) {
      logger.error(err);
    }
    // if (finalLogoutTimer.current) {
    //   clearTimeout(finalLogoutTimer.current);
    // }
    if (!sessionHasEnded) {
      checkAuthStatus(true);
    }
  };

  const returnObj: IUseMacsAuthenticator = {
    // state
    code,
    ltik,
    initialized,
    authenticated,
    authType,
    currentUserId,
    currentUserRole,
    exp,
    timeRemaining,
    refreshesRemaining,
    showExtensionDialog,
    showSessionEndDialog,
    ltiReturnUrl,
    windowMode,
    sessionId,
    studentCurrentClassId,
    launchedAssignmentId,
    isStudentView,
    exitStudentViewPath,
    iframeRef,
    isLoggingOut,
    // state updaters
    setCode,
    setLtik,
    setInitialized,
    setAuthenticated,
    setAuthType,
    setCurrentUserId,
    setExp,
    setStudentCurrentClassId,
    setRefreshesRemaining,
    setShowExtensionDialog,
    setShowSessionEndDialog,
    setLtiReturnUrl,
    setWindowMode,
    setIframeRef,
    setIsStudentView,
    setExitStudentViewPath,
    setLaunchedAssignmentId,
    // void funcs
    handleExtensionPromptLogOut,
    handleExtensionPromptExtend,
  };

  // This makes the hook's state and methods available from anywhere
  // Including inside Axios function for direct state triggers/re-renders
  MacsAuthenticator.authHook = returnObj;
  return returnObj;
}

export interface IUseMacsAuthenticator {
  // state
  code: string | null;
  ltik: string | null;
  initialized: boolean;
  authenticated: boolean;
  authType: _AuthType | null;
  currentUserId: string | null;
  currentUserRole: Role | null;
  exp: number | null;
  timeRemaining: string;
  refreshesRemaining: number | null;
  showExtensionDialog: boolean;
  showSessionEndDialog: boolean;
  ltiReturnUrl: string | null;
  windowMode: _WindowMode | null;
  sessionId: string | null;
  studentCurrentClassId: string | null;
  launchedAssignmentId: string | null;
  isStudentView: string | null;
  exitStudentViewPath: string | null;
  isLoggingOut: boolean;
  iframeRef: React.RefObject<HTMLIFrameElement> | null;
  // state updaters
  setCode: React.Dispatch<React.SetStateAction<string | null>>;
  setLtik: React.Dispatch<React.SetStateAction<string | null>>;
  setInitialized: React.Dispatch<React.SetStateAction<boolean>>;
  setAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  setAuthType: React.Dispatch<React.SetStateAction<_AuthType | null>>;
  setCurrentUserId: React.Dispatch<React.SetStateAction<string | null>>;
  setStudentCurrentClassId: React.Dispatch<React.SetStateAction<string | null>>;
  setExp: React.Dispatch<React.SetStateAction<number | null>>;
  setRefreshesRemaining: React.Dispatch<React.SetStateAction<number | null>>;
  setShowExtensionDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSessionEndDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setIsStudentView: React.Dispatch<React.SetStateAction<string | null>>;
  setExitStudentViewPath: React.Dispatch<React.SetStateAction<string | null>>;
  setLtiReturnUrl: React.Dispatch<React.SetStateAction<string | null>>;
  setWindowMode: React.Dispatch<React.SetStateAction<_WindowMode | null>>;
  setIframeRef: React.Dispatch<
    React.SetStateAction<React.RefObject<HTMLIFrameElement> | null>
  >;
  setLaunchedAssignmentId: React.Dispatch<React.SetStateAction<string | null>>;
  // void funcs
  handleExtensionPromptLogOut: () => void;
  handleExtensionPromptExtend: () => void;
}

export class MacsAuthenticator {
  public static authHook: IUseMacsAuthenticator;
}
